<template>
  <section class="service-check" :class="{'service-check--opportunity': status === 'opportunity'}" aria-label="Kontrola refinancovania" :aria-busy="busy">
    <div class="service-check__heading">
      <div>
        <strong v-if="busy"><span class="service-check__spinner" aria-hidden="true"/>Prepočítavam ponuky bánk…</strong>
        <strong v-else-if="status === 'opportunity'">Výhodnejšia sadzba · možná úspora {{ eur(best.savings) }}/mes.</strong>
        <strong v-else-if="status === 'small_saving'">Menšia úspora · {{ eur(best.savings) }}/mes.</strong>
        <strong v-else-if="status === 'no_saving'">V porovnaných ponukách teraz nie je nižšia splátka</strong>
        <strong v-else-if="status === 'missing_data'">Kontrola potrebuje doplniť údaje</strong>
        <strong v-else-if="status === 'error'">Kontrola sa nepodarila</strong>
        <strong v-else-if="status === 'inactive'">Úver už nie je aktívny</strong>
        <strong v-else>Denná kontrola refinancovania</strong>
        <p class="service-check__meta">Denne · upozornenie od 20 €/mes.<span v-if="check && check.checked_at"> · Posledná kontrola {{ checkedAt }}</span></p>
      </div>
      <button v-if="status !== 'inactive'" type="button" class="service-check__button" :disabled="busy" @click="refresh">{{ busy ? 'Prebieha kontrola' : 'Skontrolovať teraz' }}</button>
    </div>
    <p v-if="error" class="service-check__message" role="status">{{ error }}</p>
    <p v-else-if="busy" class="service-check__message" role="status">Kontrola pokračuje na pozadí, túto kartu môžete zatvoriť.</p>
    <template v-else>
      <p v-if="!active" class="service-check__message">Úver je v evidencii neaktívny, automatická kontrola sa nespúšťa.</p>
      <p v-else-if="!check" class="service-check__message">Prvá automatická kontrola ešte neprebehla. Môžete ju spustiť aj teraz.</p>
      <p v-if="result.message" class="service-check__message">{{ result.message }}</p>
      <router-link v-if="status === 'missing_data'" class="service-check__button service-check__link" :to="{name: 'KartaHypotekyDetail', params: {hypoteka: mortgage.id}}">Doplniť údaje hypotéky</router-link>
      <template v-if="best">
        <div class="service-check__offer">
          <span><strong>{{ best.bank_name }}</strong> · {{ number(best.rate) }} % · fix {{ best.fix }} r.</span>
          <span>Splátka {{ eur(result.current_payment) }} → <strong>{{ eur(best.payment) }}</strong></span>
          <span>Zostáva {{ result.months }} mesiacov</span>
        </div>
        <p class="service-check__meta">Orientačná úspora splátky pred započítaním nákladov na refinancovanie.</p>
      </template>
      <p v-if="stale" class="service-check__message">Výsledok je starší ako 48 hodín. Pred použitím spustite novú kontrolu.</p>
      <details v-if="result.assumptions" class="service-check__details">
        <summary>Podmienky a náklady porovnania<span v-if="result.compared_banks"> · {{ result.compared_banks }} bánk</span></summary>
        <p>{{ result.assumptions }}</p>
        <p v-if="best">Poplatok novej banky za poskytnutie: {{ cost(best.arrangement_fee) }}. Účet: {{ cost(best.account_fee) }}/mes. Náklady na predčasné splatenie, kataster, posudok a poistenie treba doplniť. Čistá úspora a návratnosť preto zatiaľ nie sú vyčíslené.</p>
        <p v-if="result.unavailable_banks && result.unavailable_banks.length">Nie všetky banky sa podarilo vyhodnotiť ({{ result.unavailable_banks.join(', ') }}). Záver platí len pre porovnané ponuky.</p>
      </details>
    </template>
  </section>
</template>

<script>
import api from '@/services/apiConfig';

export default {
  name: 'ServiceRefinancingCheck',
  props: {mortgage: {type: Object, required: true}, debtId: {required: true}, active: {type: Boolean, default: true}},
  data() { return {checks: this.mortgage.service_checks || [], requesting: false, error: '', polls: 0}; },
  computed: {
    check() { return this.checks.find(item => item.debt === this.debtId); },
    status() { return !this.active ? 'inactive' : this.check?.status || 'pending'; },
    busy() { return this.requesting || (this.status === 'running' && !this.timedOut); },
    timedOut() { return this.check?.started_at && Date.now() - new Date(this.check.started_at).getTime() > 30 * 60 * 1000; },
    result() { return this.check?.result || {}; },
    best() { return this.result.best; },
    checkedAt() { return new Date(this.check.checked_at).toLocaleString('sk-SK', {day:'numeric',month:'numeric',hour:'2-digit',minute:'2-digit'}); },
    stale() { return this.check?.checked_at && Date.now() - new Date(this.check.checked_at).getTime() > 48 * 60 * 60 * 1000; }
  },
  watch: {'mortgage.service_checks'(value) { this.checks = value || []; this.schedulePoll(); }},
  mounted() { this.schedulePoll(); },
  beforeDestroy() { this._disposed = true; clearTimeout(this._pollTimer); },
  methods: {
    number(value) { return Number(value).toLocaleString('sk-SK', {maximumFractionDigits: 2}); },
    eur(value) { return Number(value).toLocaleString('sk-SK', {style: 'currency', currency:'EUR'}); },
    cost(value) { return value == null ? 'nezistený' : this.eur(value); },
    schedulePoll() {
      clearTimeout(this._pollTimer);
      if (!this._disposed && this.status === 'running' && !this.timedOut && this.polls < 40) {
        this._pollTimer = setTimeout(this.poll, 3000);
      }
    },
    async poll() {
      this.polls++;
      try {
        const response = await api.get(`/mortgage/${this.mortgage.id}/service-check/`);
        if (!this._disposed) this.checks = response.data;
      } catch { this.error = 'Výsledok sa nepodarilo načítať. Kontrola môže pokračovať na pozadí; skúste kartu otvoriť znova.'; }
      this.schedulePoll();
    },
    async refresh() {
      if (this.busy) return;
      this.requesting = true; this.error = ''; this.polls = 0;
      try {
        const response = await api.post(`/mortgage/${this.mortgage.id}/service-check/`);
        if (!this._disposed) this.checks = response.data;
      } catch { this.error = 'Kontrolu sa nepodarilo spustiť. Skúste to znova.'; }
      finally { this.requesting = false; this.schedulePoll(); }
    }
  }
};
</script>

<style scoped>
.service-check {border-top:1px solid var(--app-border, #293c55); padding:16px 22px; color:var(--app-text, #ecf2ff); background:var(--app-surface, #131f30); border-radius:0 0 14px 14px; font-size:13px; line-height:1.6;}
.service-check--opportunity {border-top-color:#318970; background:color-mix(in srgb, var(--app-surface, #131f30) 92%, #48bd8d);}
.service-check--opportunity .service-check__heading strong {color:var(--app-success, #57d3a0);}
.service-check__heading {display:flex; align-items:center; justify-content:space-between; gap:16px;}
.service-check__heading strong {font-size:14px;}
.service-check__meta {font-size:11px; color:var(--app-text-muted, #a1b1c9); margin:3px 0 0;}
.service-check__button {background:transparent; border:1px solid var(--app-border, #375373); color:var(--app-primary, #69b9ff); padding:7px 12px; border-radius:9px; white-space:nowrap; cursor:pointer; font:inherit;}
.service-check__link {display:inline-block; margin-top:10px; text-decoration:none;}
.service-check__button:disabled {opacity:.65; cursor:wait;}
.service-check__button:focus-visible, summary:focus-visible {outline:2px solid var(--app-primary, #69b9ff); outline-offset:3px;}
.service-check__offer {display:flex; flex-wrap:wrap; gap:6px 24px; margin-top:10px;}
.service-check__message {margin:9px 0 0; color:var(--app-text-muted, #a1b1c9);}
.service-check__details {margin-top:8px; font-size:11px; color:var(--app-text-muted, #a1b1c9);}
.service-check__details summary {cursor:pointer;}
.service-check__details p {margin:8px 0 0; max-width:1000px;}
.service-check__spinner {display:inline-block; width:13px; height:13px; border:2px solid currentColor; border-right-color:transparent; border-radius:50%; margin-right:8px; animation:service-spin 1s linear infinite;}
@keyframes service-spin {to {transform:rotate(360deg);}}
@media(prefers-reduced-motion:reduce) {.service-check__spinner {animation:none;}}
@media(max-width:680px) {.service-check__heading {align-items:flex-start; flex-direction:column;} .service-check {padding:14px;} .service-check__offer {flex-direction:column; gap:4px;}}
</style>
